import { useEffect } from 'react'
import { DollarSign, ShoppingCart, FileText, Users, GraduationCap, BookOpen } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/ui/card'
import { Badge } from '@/ui/badge'
import { useAuthStore } from '@/core/store/authStore'
import { useClassSessionStore } from '@/features/classes/stores'
import { useSalesData } from '@/core/hooks/useSalesData'
import { SalesSummaryCards } from '@/features/finances/components/SalesSummaryCards'
import { AIInsightsWidget } from '@/features/ai/components/AIInsightsWidget'

import { StudentOverview } from './components/StudentOverview'

export function OverviewPage() {
  const isStudent = useAuthStore((s) => s.isStudent())

  if (isStudent) {
    return <StudentOverview />
  }

  return <StaffOverview />
}

function StaffOverview() {
  const { sessions, courses, loadCourses } = useClassSessionStore()
  const { orders, payments, invoices, companies, loading } = useSalesData()

  useEffect(() => {
    void loadCourses()
  }, [loadCourses])

  const activeCourses = courses.filter((c) => c.isActive !== false)

  const upcomingClasses = sessions
    .filter((s) => s.status !== 'closed')
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5)

  const revenue = payments.filter((p) => p.status === 'succeeded').reduce((s, p) => s + p.amount, 0) / 100
  const pendingInvoices = invoices.filter((i) => i.status !== 'paid').length

  const summaryCards = [
    { id: 'revenue', label: 'Revenue', value: `$${revenue.toLocaleString()}`, icon: DollarSign, color: 'text-green-600 bg-green-100' },
    { id: 'orders', label: 'Orders', value: String(orders.length), icon: ShoppingCart, color: 'text-blue-600 bg-blue-100' },
    { id: 'pending-invoices', label: 'Pending Invoices', value: String(pendingInvoices), icon: FileText, color: 'text-yellow-600 bg-yellow-100' },
    { id: 'companies', label: 'Companies', value: String(companies.length), icon: Users, color: 'text-purple-600 bg-purple-100' },
  ]

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Sales Metrics */}
      {loading ? (
        <div className="h-24 flex items-center justify-center text-gray-400">Loading...</div>
      ) : (
        <SalesSummaryCards cards={summaryCards} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Upcoming sessions table */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-semibold text-navy">Upcoming Sessions</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {upcomingClasses.length === 0 ? (
                <p className="text-sm text-slate-500 px-4 py-6 text-center">
                  No active or scheduled sessions. Create a new class to get started.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                    <thead>
                      <tr className="border-b border-slate-150 bg-gray-50">
                        <th className="text-left px-4 py-3 font-medium text-slate-500">Course</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500">Date</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500 hidden sm:table-cell">Instructor</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingClasses.map((cls) => {
                        const course = courses.find((c) => c.slug === cls.courseId)
                        return (
                          <tr key={cls.id} className="border-b border-slate-150 hover:bg-gray-50 transition-colors last:border-0">
                            <td className="px-4 py-3 font-medium text-navy">{course?.name ?? cls.courseId}</td>
                            <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{cls.date}</td>
                            <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{cls.instructorName}</td>
                            <td className="px-4 py-3">
                              <Badge
                                variant="default"
                                className={
                                  cls.status === 'active'
                                    ? 'bg-green-50 text-green-700 border border-green-100'
                                    : 'bg-orange-50 text-primary border border-orange-100'
                                }
                              >
                                {cls.status === 'active' ? 'Active' : 'Draft'}
                              </Badge>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Insights + Course catalogue */}
        <div className="space-y-4">
          <AIInsightsWidget />
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-semibold text-navy">Catalog</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {activeCourses.length === 0 ? (
                <p className="text-sm text-slate-500 px-4 py-6 text-center">No courses in the catalog.</p>
              ) : (
                activeCourses.map((course) => (
                  <div key={course.slug} className="flex items-center justify-between py-1">
                    <span className="text-sm text-navy">{course.name}</span>
                    <span className="text-xs text-slate-500">{course.validYears}a</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
